import React, { useState, useEffect } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Typography,
  CircularProgress,
  Select,
  MenuItem,
  FormControl,
  Alert
} from '@mui/material';
import { collection, query, onSnapshot, doc, updateDoc } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { format } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import { logger } from '../../utils/logger';

const DealSuggestionsTable = () => {
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all');
  const navigate = useNavigate();

  useEffect(() => {
    const q = query(collection(db, 'deal_suggestions'));
    
    const unsubscribe = onSnapshot(q, (snapshot) => {
      try {
        const suggestionsData = snapshot.docs.map(doc => {
          const data = doc.data();
          // Validate required fields
          if (!data.place_data) {
            logger.warn(`Missing place_data for suggestion ${doc.id}`);
            return null;
          }

          return {
            id: doc.id,
            ...data,
            created_at: data.created_at?.toDate() || new Date(),
            // Ensure place_data fields exist with fallbacks
            place_data: {
              name: data.place_data.name || 'Unknown Business',
              address: data.place_data.address || 'No address provided',
              ...data.place_data
            }
          };
        }).filter(Boolean); // Remove null entries

        setSuggestions(suggestionsData);
        setError(null);
      } catch (err) {
        logger.error('Error processing suggestions:', err);
        setError('Failed to load suggestions');
      } finally {
        setLoading(false);
      }
    }, (err) => {
      logger.error('Error fetching deal suggestions:', err);
      setError('Failed to fetch suggestions');
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const handleStatusChange = async (suggestionId, newStatus) => {
    try {
      await updateDoc(doc(db, 'deal_suggestions', suggestionId), {
        status: newStatus
      });
    } catch (error) {
      logger.error('Error updating suggestion status:', error);
    }
  };

  const handleCreateVenue = (suggestion) => {
    const stateData = btoa(JSON.stringify({
      initialSearchQuery: suggestion.place_data.name,
      dealDescription: suggestion.deal_description,
      suggestionId: suggestion.id,
      fromSuggestion: true
    }));

    // Log the data being passed
    logger.info('Opening add venue page with data:', {
      name: suggestion.place_data.name,
      description: suggestion.deal_description,
      id: suggestion.id
    });

    window.open(`/admin/venues/new?state=${stateData}`, '_blank');
  };

  const filteredSuggestions = suggestions.filter(suggestion => {
    if (filter === 'all') return true;
    return suggestion.status === filter;
  });

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  return (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <FormControl size="small">
          <Select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          >
            <MenuItem value="all">All Suggestions</MenuItem>
            <MenuItem value="pending">Pending</MenuItem>
            <MenuItem value="complete">Complete</MenuItem>
          </Select>
        </FormControl>
        <Typography variant="body2" color="text.secondary">
          Showing {filteredSuggestions.length} suggestions
        </Typography>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <CircularProgress />
        </Box>
      ) : filteredSuggestions.length === 0 ? (
        <Alert severity="info">No deal suggestions found</Alert>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Date</TableCell>
                <TableCell>Business</TableCell>
                <TableCell>Deal Description</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredSuggestions.map((suggestion) => (
                <TableRow key={suggestion.id}>
                  <TableCell>
                    {format(suggestion.created_at, 'MMM d, yyyy HH:mm')}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight="medium">
                      {suggestion.place_data.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block">
                      {suggestion.place_data.address}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ maxWidth: '300px', whiteSpace: 'pre-wrap' }}>
                    {suggestion.deal_description || 'No description provided'}
                  </TableCell>
                  <TableCell>
                    <Select
                      size="small"
                      value={suggestion.status || 'pending'}
                      onChange={(e) => handleStatusChange(suggestion.id, e.target.value)}
                    >
                      <MenuItem value="pending">Pending</MenuItem>
                      <MenuItem value="complete">Complete</MenuItem>
                      <MenuItem value="rejected">Rejected</MenuItem>
                    </Select>
                  </TableCell>
                  <TableCell>
                    <Button
                      variant="contained"
                      size="small"
                      onClick={() => handleCreateVenue(suggestion)}
                      disabled={suggestion.status === 'complete'}
                    >
                      Create Venue
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default DealSuggestionsTable;